import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import HeroSlider from "../components/home/HeroSlider";
import AboutSection from "../components/home/AboutSection";
import FeaturedProjects from "../components/home/FeaturedProjects";
import MediaCenterSection from "../components/home/MediaCenterSection";
import PartnersMarquee from "../components/home/PartnersMarquee";
import BitrixContactForm from "../components/home/BitrixContactForm";
import GroupCompaniesSection from "../components/home/GroupCompaniesSection";
import { Reveal, SectionEyebrow } from "../components/common/Section";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";

const HomePage = () => {
  const { t, pick } = useI18n();
  const { settings } = useOutletContext() || {};
  const [homepage, setHomepage] = useState(null);
  const [projects, setProjects] = useState([]);
  const [news, setNews] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [h, p, n, gc] = await Promise.all([
          api.get("/public/homepage").then((r) => r.data),
          api.get("/public/projects?featured=true").then((r) => r.data),
          api.get("/public/news").then((r) => r.data),
          api.get("/public/group-companies?featured=true").then((r) => r.data),
        ]);
        setHomepage(h);
        setProjects(p);
        setNews(n);
        setCompanies(gc);
      } catch (e) {
        console.error(e);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const hasHero = !!(homepage?.hero_slides?.length || homepage?.hero);

  return (
    <div data-testid="home-page">
      {hasHero ? (
        <HeroSlider slides={homepage?.hero_slides || []} fallbackHero={homepage?.hero} settings={settings} />
      ) : (
        /* Reserved-space placeholder so the rest of the page doesn't scroll
           up while data is loading. Prevents the "contact section flashes at top" bug. */
        <section
          className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
          data-testid="home-hero-placeholder"
          style={{
            background:
              "linear-gradient(135deg, #101F4A 0%, #22346B 55%, #1A2A5A 100%)",
          }}
        >
          <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        </section>
      )}
      {loaded ? <AboutSection data={homepage} /> : null}
      {loaded ? <MediaCenterSection items={news} /> : null}
      {loaded ? <GroupCompaniesSection companies={companies} /> : null}
      {loaded ? <FeaturedProjects projects={projects} /> : null}
      {loaded ? <PartnersMarquee partners={homepage?.partners} /> : null}

      {/* Contact snapshot — premium editorial layout */}
      <section className="relative bg-white overflow-hidden" data-testid="home-contact-section">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
          <div className="absolute inset-x-0 top-1/3 h-px bg-brand-navy" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-primary" />
        </div>

        <div className="container-x py-6 md:py-10 relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          {/* Left — Contact info */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-12 bg-brand-primary" />
                <span className="text-[0.7rem] tracking-[0.36em] uppercase text-brand-primary font-medium">
                  {t("nav.contact")}
                </span>
              </div>
              <h2
                className="font-serif text-brand-navy leading-[1.02] tracking-tight"
                style={{ fontSize: "clamp(2rem, 4.2vw, 3.5rem)" }}
              >
                {pick({
                  tr: "Konuşalım. Birlikte inşa edelim.",
                  en: "Let’s talk. Let’s build together.",
                  ar: "لنتحدث. لنبنِ معاً.",
                })}
              </h2>
              <p className="mt-6 text-brand-medium max-w-lg leading-relaxed">
                {pick({
                  tr: "Ekibimiz 3 kıtada, projeleriniz ve yatırımlarınız için hazır. Aşağıdan bir konu seçin — size 24 saat içinde dönüş yapalım.",
                  en: "Our team stands ready across 3 continents for your projects and investments. Pick a topic — we respond within 24 hours.",
                  ar: "فريقنا جاهز عبر 3 قارات لمشاريعك واستثماراتك. اختر موضوعاً — نعود إليك خلال 24 ساعة.",
                })}
              </p>

              <div className="mt-10 space-y-0">
                {settings?.contact_phone ? (
                  <a
                    href={`tel:${settings.contact_phone.replace(/\s/g, "")}`}
                    className="group flex items-center gap-4 py-4 border-t border-black/10 hover:border-brand-primary transition-colors"
                    data-testid="contact-phone-link"
                  >
                    <span className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center group-hover:bg-brand-navy group-hover:text-white transition-colors duration-300">
                      <Phone size={16} strokeWidth={1.5} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-medium">
                        {pick({ tr: "Telefon", en: "Phone", ar: "الهاتف" })}
                      </div>
                      <div className="mt-0.5 font-serif text-lg md:text-xl text-brand-navy">
                        {settings.contact_phone}
                      </div>
                    </div>
                  </a>
                ) : null}

                {settings?.whatsapp_number ? (
                  <a
                    href={`https://wa.me/${settings.whatsapp_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 py-4 border-t border-black/10 hover:border-[#25D366] transition-colors"
                    data-testid="contact-whatsapp-link"
                  >
                    <span className="w-10 h-10 rounded-full bg-[#25D366]/15 text-[#128C7E] flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white transition-colors duration-300">
                      <MessageCircle size={16} strokeWidth={1.5} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-medium">
                        WhatsApp
                      </div>
                      <div className="mt-0.5 font-serif text-lg md:text-xl text-brand-navy">
                        +90 533 728 01 02
                      </div>
                    </div>
                  </a>
                ) : null}

                {settings?.contact_email ? (
                  <a
                    href={`mailto:${settings.contact_email}`}
                    className="group flex items-center gap-4 py-4 border-t border-b border-black/10 hover:border-brand-primary transition-colors"
                    data-testid="contact-email-link"
                  >
                    <span className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center group-hover:bg-brand-navy group-hover:text-white transition-colors duration-300">
                      <Mail size={16} strokeWidth={1.5} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-medium">
                        {pick({ tr: "E-posta", en: "Email", ar: "البريد الإلكتروني" })}
                      </div>
                      <div className="mt-0.5 font-serif text-lg md:text-xl text-brand-navy break-all">
                        {settings.contact_email}
                      </div>
                    </div>
                  </a>
                ) : null}
              </div>

              {(settings?.offices || []).length ? (
                <div className="mt-12">
                  <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-medium mb-5">
                    {pick({ tr: "Global Ofisler", en: "Global Offices", ar: "مكاتبنا حول العالم" })}
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                    {(settings.offices || []).map((o, i) => (
                      <div key={i} className="flex items-start gap-2.5" data-testid={`home-office-${i}`}>
                        <MapPin size={14} strokeWidth={1.5} className="text-brand-primary mt-1 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-serif text-brand-navy text-sm md:text-base leading-tight">
                            {pick(o.name, "")}
                          </div>
                          {o.phone ? (
                            <div className="text-brand-medium text-xs mt-0.5 truncate">
                              {o.phone}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </Reveal>
          </div>

          {/* Right — Form panel with shadow */}
          <div className="lg:col-span-7 relative">
            <div className="relative bg-white shadow-xl lg:shadow-2xl border border-black/5 p-6 md:p-10 lg:p-12">
              <BitrixContactForm forms={settings?.bitrix_forms || []} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
